import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, AuthState } from '../types';
import { storage } from '../utils/storage';
import { initSessionGuard } from '../utils/sessionGuard';

const STORAGE_KEY = 'auth-storage';
const REMEMBER_KEY = 'auth-remember-me';

// Use the right storage on app init. Default OFF (session-only) — only an
// explicit remember-me opt-in ('true') persists tokens to localStorage across
// browser restarts. Previously defaulted ON, which left refresh tokens in
// localStorage on shared/demo machines.
const remembered = localStorage.getItem(REMEMBER_KEY) === 'true';
storage.changeStorage(remembered ? localStorage : sessionStorage);
// Workaround for sessionStorage: clean it up after restoring the closed tab
initSessionGuard();

interface AuthActions {
  setUser: (user: User, token: string, refreshToken: string) => void;
  setTokens: (token: string, refreshToken: string) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
}

type AuthStore = AuthState & { refreshToken: string | null } & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    set => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      setUser: (user: User, token: string, refreshToken: string) => {
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      setTokens: (token: string, refreshToken: string) =>
        set({
          token,
          refreshToken,
        }),

      updateUser: (updates: Partial<User>) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      logout: () => {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(REMEMBER_KEY);
        storage.changeStorage(localStorage);
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => storage),
      partialize: state => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
